import React, { useState } from 'react';
import Box from 'ui/Box';
import { useSelector } from 'react-redux';
import { getSelectedProduct, SelectedProduct } from 'ducks/products';
import styled from 'styled-components';
import { ProductThumb, ThumbImage } from 'selectedMenu/components';
import { useCore } from 'contexts/core';
import { getSelectedEntity } from 'ducks/core';
import { useIntl } from 'react-intl';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { isMobile } from 'utils/platform';
import { useBehaviorTracker } from 'contexts/behaviors';
import { ProductEntity } from '@inter-ikea-storageone/range';
import { getMPPImage } from 'products';
import { extractImageSource } from 'utils/imageUtils';
import Title from 'ui/Typography/Title';
import SkapaTooltip from 'ui/Tooltip/SkapaTooltip';
import { KompisCarousel, KompisCarouselItem } from '@inter-ikea-kompis/react-components';
import { e2e } from 'utils/queryUtils';
import { CarouselLayoutEnum } from '@inter-ikea-kompis/component-carousel';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { SwipeEvent } from 'components/MobileSwiper/MobileSwiper';
import ProductImage from 'components/ProductImage/ProductImage';

const CarouselWrapper = styled.div`
  max-width: 100vw;
  overflow: hidden;
  width: 100%;
  padding: 0 ${({ theme }) => theme.space.md};
  list-style-type: none;
`;

const Thumbnails = styled.ul`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;
  margin: 0;
  padding-left: 0;
  min-height: 120px;
`;

const StyledButton = styled.button`
  position: relative;
  background: #fff;
  cursor: pointer;
  width: 100%;
  border: none;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0), 0 0 0 rgba(0, 0, 0, 0);

  @media (hover: hover) {
    &:hover {
      box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1), 0 8px 8px rgba(0, 0, 0, 0.04);
    }
  }
  &:focus {
    outline: 0.125rem solid ${({ theme }) => theme.colors.feedback.focus};
  }
`;

const ItemWrapper = styled.div`
  position: relative;
  overflow: visible;
  width: 4.5rem;
  margin: 0.3rem 0.25rem;
`;

const getSolutionIds = (
  selectedProduct: SelectedProduct | null,
  selectedEntity?: ProductEntity
) => {
  if (!selectedEntity || !selectedProduct) return { leftSolutionId: '', rightSolutionId: '' };

  const split = selectedEntity.productId.split('-');
  const leftSolutionId = `${split[0]}--default`;
  const rightSolutionId = `${split[0]}--mirrored`;

  return { leftSolutionId, rightSolutionId };
};

const ChangeOrientation = ({ ...props }) => {
  const coreApi = useCore();
  const { formatMessage } = useIntl();
  const selectedProduct = useSelector(getSelectedProduct);
  const selectedEntity = useSelector(getSelectedEntity);
  const behaviorTracker = useBehaviorTracker();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { leftSolutionId, rightSolutionId } = getSolutionIds(selectedProduct, selectedEntity);

  const options = [
    {
      id: leftSolutionId,
      toolTip: 'storageone.left',
      styleTransform: ''
    },
    {
      id: rightSolutionId,
      toolTip: 'storageone.right',
      styleTransform: 'scaleX(-1)'
    }
  ];

  const onSelect = (solutionId: string) =>
    behaviorTracker.custom(() => {
      if (!selectedEntity || solutionId === selectedEntity.productId) {
        return;
      }
      const newSolutionId = solutionId === leftSolutionId ? leftSolutionId : rightSolutionId;

      const replacePlacementResult = coreApi.products.replace(
        selectedEntity.entityId,
        newSolutionId
      );

      if (
        replacePlacementResult?.replacedEntityResult.valid &&
        replacePlacementResult.replacedEntityResult.placedEntity
      ) {
        coreApi.entities.select(replacePlacementResult.replacedEntityResult.placedEntity);
      }
    });

  if (!selectedProduct) return;
  const solutionImage = getMPPImage(selectedProduct);
  if (!solutionImage) return;
  const { srcset, fallbackImage } = extractImageSource(solutionImage.sources);

  const onSwipe = (event: SwipeEvent) => {
    setSelectedIndex(event.detail.selectedIndex);
  };

  if (isMobile()) {
    return (
      <CarouselWrapper>
        <KompisCarousel
          showArrows={!e2e()}
          selectedIndex={selectedIndex}
          layout={CarouselLayoutEnum.overflow}
          theme={SkapaTheme}
          onChange={onSwipe}
        >
          {options.map(option => {
            const selected = selectedEntity?.productId === option.id;
            return (
              <KompisCarouselItem key={option.id} data-testid={`swiper-item-${option.id}`}>
                <ItemWrapper>
                  <ProductImage
                    sizes="5rem"
                    image={solutionImage}
                    alt={formatMessage({ id: option.toolTip })}
                    onClick={() => onSelect(option.id)}
                    style={{
                      border: selected ? '1px solid black' : '1px solid transparent',
                      transform: option.styleTransform
                    }}
                  />
                </ItemWrapper>
              </KompisCarouselItem>
            );
          })}
        </KompisCarousel>
      </CarouselWrapper>
    );
  }

  return (
    <Box mt="s">
      {!isMobile() && (
        <Title type={ThemeFontStyleTypeEnum.headingSmall}>
          {formatMessage({ id: 'storageone.changeOrientation' })}
        </Title>
      )}
      <Thumbnails {...props}>
        {options.map(option => (
          <ProductThumb
            key={option.id}
            data-testid={option.id}
            onClick={() => onSelect(option.id)}
            data-selected={selectedEntity?.productId === option.id}
          >
            <SkapaTooltip
              position="top"
              tooltipText={formatMessage({ id: option.toolTip })}
              interactiveCustomTrigger
            >
              <StyledButton>
                <ThumbImage
                  src={fallbackImage.src}
                  srcSet={srcset}
                  alt={formatMessage({ id: option.toolTip })}
                  style={{ transform: option.styleTransform }}
                ></ThumbImage>
              </StyledButton>
            </SkapaTooltip>
          </ProductThumb>
        ))}
      </Thumbnails>
    </Box>
  );
};

export default ChangeOrientation;
